const feature = {
  slug: "instagram-story-reply-automation",
  path: "/features/instagram-story-reply-automation",
  eyebrow: "Instagram Story reply automation",
  h1: "Turn one word on your Instagram Story into a product card",
  seoTitle: "Instagram Story Reply Automation | Checkout DM",
  description:
    "When a customer replies to your Story with a keyword, Checkout DM sends the product card and carries the order through checkout in the same chat.",
  intro:
    "Someone replying to your Story is already looking at your product. Story reply automation answers in seconds, while the Story is still live, and takes that reply all the way to a paid order without you typing.",
  glance: [
    { label: "Free, Creator and Starter", value: "Generic Story replies" },
    { label: "Growth and Pro", value: "Each keyword matches its own product" },
    { label: "Story length", value: "24 hours, so speed matters" },
  ],
  blocks: [
    { type: "h2", text: "Why Story replies are worth automating" },
    {
      type: "p",
      text:
        "A Story reply is a buyer raising their hand. The trouble is timing. Replies land in a busy inbox, your answer arrives hours later, and the Story has expired by then. Automation closes that gap, because the buyer gets the product immediately instead of waiting for you to open your inbox.",
    },

    { type: "h2", text: "How a Story reply becomes an order" },
    {
      type: "ol",
      items: [
        "**The buyer replies with a keyword.** It can be a colour like Red, a word like Link, or anything you set in the Story Automation panel of your dashboard.",
        "**The product card arrives instantly.** The buyer receives the product you attached to that keyword.",
        "**They choose details.** They tap the card and pick a size and quantity, so nobody has to type a size on your Story.",
        "**The item goes into the cart.** The buyer is asked whether to add more to the same cart.",
        "**Checkout.** They enter their name, phone number, email and billing address. On Growth and Pro with coupons active, they can also enter a coupon code.",
        "**Payment.** They pay through a payment link, with shipping added on the payment screen according to your shipping rules.",
        "**After the sale.** You ship the order. If you allow returns or exchanges, the customer can start one from the same DM thread.",
      ],
    },
    {
      type: "chat",
      shop: "yourshop",
      title: "Story reply",
      caption: "Illustrative example. Your own message uses your products and wording.",
      messages: [
        { from: "time", text: "Replied to your Story" },
        { from: "buyer", text: "Blue" },
        { from: "shop", text: "[Product card] Blue Linen Kurta Set · ₹1,899 · Select this" },
        { from: "buyer", text: "Select this" },
        { from: "shop", text: "Which size and how many would you like?" },
        { from: "buyer", text: "M, 1" },
        { from: "shop", text: "Added to your cart. Would you like to add anything else before checkout?" },
      ],
    },

    { type: "h2", text: "Two keywords, two products, one Story" },
    {
      type: "p",
      text:
        "On Growth and Pro, each keyword can match its own product, and a single Story can carry up to two keywords with two different products. That lets you show a pair of items and let customers pick one by replying with the word you printed next to it. Free, Creator and Starter use generic Story replies instead of product-matched ones. See [plans and prices](/pricing) for the full list.",
    },

    { type: "h2", text: "How to design a Story that gets replies" },
    {
      type: "ul",
      items: [
        "**One product per keyword**, with one clear image and one price. A Story with six products gets few replies.",
        "**Show the price on screen.** It saves a question and filters out people who are not ready to buy.",
        "**Tell people the exact word to send**, such as “Reply RED for the red one”.",
        "**Use real urgency only**, like limited stock or a real end date.",
      ],
    },
    {
      type: "p",
      text:
        "Our guide to [Story replies that sell](/blog/instagram-story-replies-that-sell) covers Story design and wording in more detail.",
    },

    { type: "h2", text: "Rules it follows" },
    {
      type: "p",
      text:
        "A Story reply is a message from the customer, so Meta’s 24-hour conversation window applies. Checkout DM replies within that window and connects through Instagram’s official login, so your password is never shared. Our guide to [whether Instagram DM automation is safe](/blog/is-instagram-dm-automation-safe-2026) lists the limits in full.",
    },

    { type: "h2", text: "What it does not do" },
    {
      type: "ul",
      items: [
        "**It only starts from a reply.** Viewers who watch but never reply do not receive a message.",
        "**It sells what is in your catalog.** Keep stock up to date, so a Story never promotes something you cannot ship.",
        "**It runs inside Instagram.** Story replies are an Instagram feature, so this automation does not apply to WhatsApp or other channels.",
      ],
    },
  ],
  related: [
    { to: "/features/instagram-dm-automation", title: "Instagram DM automation", description: "The full chat flow from first message to recorded order." },
    { to: "/features/instagram-comment-to-dm-automation", title: "Comment-to-DM automation", description: "The same idea for comments on posts and Reels." },
    { to: "/features/instagram-product-catalog-checkout", title: "Catalog and checkout", description: "How products, sizes and payment work in the chat." },
    { to: "/pricing", title: "Checkout DM pricing", description: "Which plan includes product-matched Story replies." },
  ],
  faqs: [
    {
      q: "What is Instagram Story reply automation?",
      a: "It sends an automatic private reply when someone replies to your Story with a keyword. With Checkout DM, that reply is a product card, and the conversation continues through size, cart, delivery details and payment.",
    },
    {
      q: "Which plan includes product-matched Story replies?",
      a: "Growth and Pro. On those plans each keyword can match its own product, and one Story can carry up to two keywords. Other plans use generic Story replies.",
    },
    {
      q: "Does the buyer have to type their size?",
      a: "No. They tap the product card and choose size and quantity in the chat, so nobody has to type a size on your Story.",
    },
    {
      q: "Does it work if the customer only views my Story?",
      a: "No. It starts when a customer replies to the Story. Viewing alone does not trigger a message.",
    },
    {
      q: "Is Story reply automation allowed by Instagram?",
      a: "Checkout DM connects through Meta’s official login and replies within Meta’s published conversation limits. Our safety guide explains the rules in detail.",
    },
  ],
};

export default feature;
